import { useQueries } from "@tanstack/react-query";
import { useTRPC } from "../../lib/api";
import { useMe } from "../../lib/me";
import { memberLabel } from "../tasks/use-task-actions";
import { cx } from "../../ui";
import { Icon } from "../../ui/icons";
import { eventText, type ActivityItem } from "./activity-model";
import { relativeDate } from "./model";

/** People's names across the workspaces the rows come from (a row only stores uids). */
export function useNames(workspaceIds: readonly string[]): (uid: string) => string {
  const trpc = useTRPC();
  const ids = [...new Set(workspaceIds)].sort();
  const results = useQueries({ queries: ids.map((workspaceId) => trpc.workspaces.members.queryOptions({ workspaceId })) });
  const byUid = new Map<string, string>();
  for (const r of results) for (const m of r.data === undefined ? [] : r.data) byUid.set(m.uid, memberLabel(r.data, m.uid));
  return (uid: string) => byUid.get(uid) ?? "Someone";
}

/**
 * A chat or task in the inbox list: who and what happened on the first line, the message or the
 * task on the second, a chat or task mark where mail rows have their mailbox colour.
 */
export function ActivityRow({
  item,
  unread,
  actor,
  now,
  current,
  rowRef,
  onFocus,
  onOpen,
}: {
  item: ActivityItem;
  unread: boolean;
  actor: string;
  now: Date;
  current: boolean;
  rowRef: (el: HTMLButtonElement | null) => void;
  onFocus: () => void;
  onOpen: () => void;
}) {
  const me = useMe();
  const workspace = me.workspaces.find((w) => w.id === item.workspaceId)?.name;
  const isTask = item.kind === "task";
  const second = isTask ? item.title : item.snippet;
  const third = isTask ? item.snippet : "";
  return (
    <button
      ref={rowRef}
      type="button"
      data-activity-id={item.id}
      aria-current={current ? "true" : undefined}
      onFocus={onFocus}
      onClick={onOpen}
      className={cx("flex h-full min-w-0 flex-1 items-start gap-3 border-b border-border px-3 py-2 text-left hover:bg-surface-2", current && "bg-surface-2")}
    >
      <span
        aria-hidden="true"
        className={cx("mt-0.5 grid size-6 shrink-0 place-items-center rounded-full", isTask ? "bg-surface-2 text-text" : "bg-surface-2 text-accent")}
      >
        <Icon name={isTask ? "task" : "chat"} className="size-3.5" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="flex items-baseline gap-2">
          <span className={cx("min-w-8 shrink-[999] truncate text-sm text-text", unread && "font-semibold")}>
            {unread ? <span className="sr-only">Unread, </span> : null}
            <span className="sr-only">{isTask ? "Task: " : "Chat: "}</span>
            {actor}
          </span>
          <span className="min-w-0 truncate text-xs text-muted">
            {eventText(item)}
            {workspace === undefined ? null : ` · ${workspace}`}
          </span>
          <span className="flex-1" />
          <time dateTime={item.lastAt.toISOString()} className={cx("shrink-0 text-xs", unread ? "font-semibold text-text" : "text-muted")}>
            {relativeDate(item.lastAt, now)}
          </time>
        </span>
        <span className={cx("min-w-0 truncate text-sm text-text", unread && "font-semibold")}>{second === "" ? eventText(item) : second}</span>
        <span className="min-w-0 truncate text-xs text-muted">{third}</span>
      </span>
    </button>
  );
}
